import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { auctionService, cardService } from '../services/api';

interface Card {
  id: number;
  name: string;
  imageUrl?: string;
  rarity?: string;
  type?: string;
  cardNumber?: string;
  startPrice?: string;
  minIncrement?: string;
}

const formatDateTimeLocal = (date: Date) => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const CreateAuctionPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  // Replaced single cardId state with array of selected cards
  const [selectedCards, setSelectedCards] = useState<Card[]>([]);
  const [cardResults, setCardResults] = useState<Card[]>([]);
  const [cardSearch, setCardSearch] = useState('');
  const [searchingCards, setSearchingCards] = useState(false);
  const [description, setDescription] = useState('');
  const [facebookLink, setFacebookLink] = useState('');

  const [autoExtendMinutes, setAutoExtendMinutes] = useState(5);
  const [endTime, setEndTime] = useState(() => {
    const inTwoDays = new Date();
    inTwoDays.setDate(inTwoDays.getDate() + 2);
    return formatDateTimeLocal(inTwoDays);
  });
  const [images, setImages] = useState<FileList | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate global fields
    if (!endTime) {
      setError(t('createAuction.errors.requiredFields'));
      return;
    }

    if (!facebookLink) {
      setError(t('createAuction.errors.facebookRequired'));
      return;
    }

    if (selectedCards.length === 0) {
      setError(t('createAuction.errors.selectCard'));
      return;
    }

    // Validate per-card fields
    const missingPrice = selectedCards.find(c => !c.startPrice);
    if (missingPrice) {
      setError(`${t('createAuction.errors.priceRequiredFor')} ${missingPrice.name}`);
      return;
    }

    try {
      setSubmitting(true);
      const createdIds: number[] = [];

      // Create an auction for EACH selected card
      // Generate a unique batchId for this set of auctions
      const batchId = crypto.randomUUID();

      for (const card of selectedCards) {
        const formData = new FormData();

        // Single card per auction
        formData.append('cardIds[]', String(card.id));

        // Per-card title or global title
        const auctionTitle = title || card.name;
        formData.append('title', auctionTitle);

        if (description) formData.append('description', description);
        formData.append('facebookLink', facebookLink);

        formData.append('startPrice', card.startPrice || '0');
        formData.append('minIncrement', card.minIncrement || '10');
        formData.append('endTime', new Date(endTime).toISOString());
        formData.append('autoExtendMinutes', String(autoExtendMinutes));
        formData.append('batchId', batchId);

        if (images) {
          Array.from(images).forEach((file) => {
            formData.append('images', file);
          });
        }

        const response = await auctionService.createAuction(formData);
        if (response.data?.data?.id) {
          createdIds.push(response.data.data.id);
        }
      }

      // If only one, redirect to it. If multiple, redirect to list.
      if (createdIds.length === 1) {
        navigate(`/auctions/${createdIds[0]}`);
      } else {
        navigate('/auctions');
      }

    } catch (err: any) {
      const message = err?.response?.data?.message || t('createAuction.errors.generic');
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const searchCards = async () => {
    if (!cardSearch || cardSearch.trim().length < 2) {
      setCardResults([]);
      return;
    }
    try {
      setSearchingCards(true);
      const response = await cardService.getCards({ search: cardSearch.trim(), limit: 10 });
      setCardResults(response.data?.data || response.data || []);
    } catch (err) {
      console.error('Failed to search cards', err);
      setCardResults([]);
    } finally {
      setSearchingCards(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      searchCards();
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardSearch]);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      navigate('/login');
    }
    // removed role check
  }, [t, navigate]);

  const handleSelectCard = (card: Card) => {
    // Prevent duplicates
    if (!selectedCards.find(c => c.id === card.id)) {
      const newSelection = [...selectedCards, { ...card, minIncrement: '10', startPrice: '' }];
      setSelectedCards(newSelection);
      // Removed single-card title autofill as it might be confusing in batch mode
    }
    setCardSearch(''); // Clear search after selection
    setCardResults([]);
  };

  const handleRemoveCard = (cardId: number) => {
    setSelectedCards(prev => prev.filter(c => c.id !== cardId));
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold mb-2">{t('createAuction.title')}</h1>
        <p className="text-sm text-gray-600 mb-6">{t('createAuction.subtitle')}</p>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('createAuction.form.title')}</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('createAuction.form.titlePlaceholder')}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">{t('createAuction.form.selectCard')}</label>

            {/* Selected Cards List with Pricing */}
            {selectedCards.length > 0 && (
              <div className="space-y-3 mb-4">
                {selectedCards.map((card) => (
                  <div key={card.id} className="p-4 border border-blue-200 bg-blue-50 rounded-lg flex flex-col sm:flex-row gap-4">
                    <div className="flex items-center gap-3">
                      {card.imageUrl && (
                        <img src={card.imageUrl} alt={card.name} className="w-12 h-16 object-cover rounded border" />
                      )}
                      <div>
                        <p className="font-semibold text-gray-800 text-sm">{card.name}</p>
                        <p className="text-xs text-gray-500">#{card.cardNumber || '—'}</p>
                      </div>
                    </div>

                    <div className="flex-1 grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{t('createAuction.form.startPrice')} *</label>
                        <input
                          type="number"
                          min="0"
                          value={card.startPrice || ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            setSelectedCards(prev => prev.map(c => c.id === card.id ? { ...c, startPrice: val } : c));
                          }}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                          placeholder="0.00"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{t('createAuction.form.minIncrement')}</label>
                        <input
                          type="number"
                          min="1"
                          value={card.minIncrement || '10'}
                          onChange={(e) => {
                            const val = e.target.value;
                            setSelectedCards(prev => prev.map(c => c.id === card.id ? { ...c, minIncrement: val } : c));
                          }}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                          placeholder="10"
                        />
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleRemoveCard(card.id)}
                      className="text-red-500 hover:text-red-700 p-1 self-start sm:self-center"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            <input
              type="text"
              value={cardSearch}
              onChange={(e) => setCardSearch(e.target.value)}
              placeholder={t('createAuction.form.searchPlaceholder')}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {cardResults.length > 0 && (
              <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg divide-y bg-white absolute z-10 w-full max-w-2xl shadow-lg">
                {cardResults.map((card) => (
                  <button
                    key={card.id}
                    type="button"
                    onClick={() => handleSelectCard(card)}
                    className="w-full text-left px-4 py-3 hover:bg-blue-50 flex items-center gap-3"
                  >
                    {card.imageUrl && (
                      <img src={card.imageUrl} alt={card.name} className="w-12 h-16 object-cover rounded border" />
                    )}
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800">{card.name}</p>
                      <p className="text-xs text-gray-500">
                        #{card.cardNumber || '—'} • {card.rarity || t('createAuction.form.unknown')} • {card.type || t('createAuction.form.type')}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {searchingCards && <p className="text-xs text-gray-500">{t('createAuction.form.searching')}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('createAuction.form.facebookLink')} ({t('common.required')})</label>
            <input
              type="url"
              value={facebookLink}
              onChange={(e) => setFacebookLink(e.target.value)}
              placeholder="https://facebook.com/..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
            <p className="text-xs text-gray-500 mt-1">{t('createAuction.form.facebookHelp')}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('createAuction.form.description')}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder={t('createAuction.form.descPlaceholder')}
            />
          </div>

          <div className="grid md:grid-cols-3 gap-4">

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('createAuction.form.autoExtend')}</label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  value={autoExtendMinutes}
                  onChange={(e) => setAutoExtendMinutes(Number(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <span className="absolute right-3 top-3 text-gray-500 text-sm">{t('common.minutes')}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">{t('createAuction.form.autoExtendHelp')}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('createAuction.form.endTime')}</label>
              <input
                type="datetime-local"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('createAuction.form.images')}</label>
            <div className="flex items-center">
              <label
                htmlFor="auction-images"
                className="cursor-pointer bg-white py-2 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {t('common.chooseImages')}
              </label>
              <input
                id="auction-images"
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => setImages(e.target.files)}
                className="sr-only"
              />
              <span className="ml-3 text-sm text-gray-500">
                {images && images.length > 0
                  ? `${images.length} ${t('common.chooseFile') === 'เลือกไฟล์' ? 'ไฟล์ถูกเลือก' : 'files selected'}`
                  : t('common.noFileChosen')}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">{t('createAuction.form.imagesHelp')}</p>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className={`w-full py-3 rounded-lg font-semibold transition ${submitting ? 'bg-gray-300 text-gray-600' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
          >
            {submitting ? t('createAuction.form.submitting') : t('createAuction.form.submit')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateAuctionPage;
